import { OverQuotaNotice } from "@components/OverQuotaNotice";
import { zodResolver } from "@hookform/resolvers/zod";
import { formatBp } from "@lib/genetics";
import { Button } from "@ui/button";
import { DialogFooter } from "@ui/dialog";
import { Input } from "@ui/input";
import { Label } from "@ui/label";
import { ScrollArea } from "@ui/scroll-area";
import { Switch } from "@ui/switch";
import { Textarea } from "@ui/textarea";
import { Check, Loader2, RotateCcw, X } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import type { AccessionItem, AccessionStatus } from "./types";
import { useAccessionImport } from "./useAccessionImport";

const accessionFormSchema = z.object({
  input: z.string(),
  collectionName: z.string(),
});

type AccessionFormData = z.infer<typeof accessionFormSchema>;

type AccessionFormProps = {
  onClose: () => void;
};

export function AccessionForm({ onClose }: AccessionFormProps) {
  const { register, watch, setValue } = useForm<AccessionFormData>({
    resolver: zodResolver(accessionFormSchema),
    defaultValues: { input: "", collectionName: "" },
  });

  const [asCollection, setAsCollection] = useState(false);

  const input = watch("input");
  const collectionName = watch("collectionName");

  const {
    fetching,
    isOverQuota,
    accessions,
    visibleItems,
    unfetchedAccessions,
    successCount,
    canFetch,
    canCreate,
    hasMultiple,
    defaultCollectionName,
    handleFetch,
    handleSubmit,
  } = useAccessionImport({ input, collectionName, asCollection, onClose });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <div className="flex h-80 flex-col gap-2 py-4">
        <p className="text-muted-foreground text-sm">
          Fetch sequences from NCBI by accession.
        </p>
        <div className="grid min-h-0 flex-1 grid-cols-2 gap-4">
          <div className="flex min-h-0 flex-col gap-2">
            {asCollection && hasMultiple && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="accession-collection-name">
                  Collection Name
                </Label>
                <Input
                  id="accession-collection-name"
                  {...register("collectionName")}
                />
              </div>
            )}
            <Textarea
              {...register("input")}
              placeholder="Enter accessions (comma or newline separated)&#10;e.g. NC_001416, NC_001422"
              className="min-h-0 flex-1 resize-none font-mono"
              disabled={fetching}
            />
          </div>
          <AccessionList
            items={visibleItems}
            onRetry={(accession) => handleFetch([accession])}
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex flex-1 items-center justify-between text-muted-foreground text-sm">
            <span>
              {isOverQuota ? (
                <OverQuotaNotice />
              ) : accessions.length === 0 ? (
                "\u00A0"
              ) : visibleItems.length > 0 ? (
                `${successCount} of ${visibleItems.length} fetched successfully`
              ) : (
                `${accessions.length} accession${accessions.length > 1 ? "s" : ""}`
              )}
            </span>
            <span>Over 10 Mbp? Import as a FASTA file.</span>
          </div>
          {hasMultiple && (
            <div className="flex items-center gap-2">
              <Label htmlFor="accession-collection-toggle">
                Create as collection
              </Label>
              <Switch
                id="accession-collection-toggle"
                checked={asCollection}
                onCheckedChange={(checked) => {
                  setAsCollection(checked);
                  if (checked && !collectionName) {
                    setValue("collectionName", defaultCollectionName);
                  }
                }}
              />
            </div>
          )}
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        {canFetch && (
          <Button
            type="button"
            variant={canCreate ? "outline" : "default"}
            onClick={() => handleFetch()}
            disabled={fetching}
          >
            {fetching && <Loader2 className="animate-spin" />}
            {unfetchedAccessions.length > 0 ? "Fetch" : "Retry Failed"}
          </Button>
        )}
        {canCreate && (
          <Button type="submit">
            {asCollection && hasMultiple
              ? "Create Collection"
              : `Create ${successCount} Sequence${successCount !== 1 ? "s" : ""}`}
          </Button>
        )}
      </DialogFooter>
    </form>
  );
}

type AccessionListProps = {
  items: AccessionItem[];
  onRetry: (accession: string) => void;
};

function AccessionList({ items, onRetry }: AccessionListProps) {
  const isEmpty = items.length === 0;

  return (
    <div className="h-full overflow-hidden rounded-md border">
      {isEmpty ? (
        <div className="flex h-full items-center justify-center bg-muted text-muted-foreground text-sm">
          Preview
        </div>
      ) : (
        <ScrollArea className="h-full">
          <div className="divide-y">
            {items.map((item) => (
              <AccessionRow
                key={item.accession}
                item={item}
                onRetry={onRetry}
              />
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}

type AccessionRowProps = {
  item: AccessionItem;
  onRetry: (accession: string) => void;
};

function AccessionRow({ item, onRetry }: AccessionRowProps) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 text-sm">
      <StatusIcon status={item.status} />
      <span className="min-w-0 flex-1 truncate font-medium">
        {item.accession}
      </span>
      {item.status === "complete" && item.record && (
        <span className="text-muted-foreground">
          {formatBp(item.record.sequence.length)}
        </span>
      )}
      {item.status === "error" && (
        <>
          <span className="text-destructive">{item.error}</span>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => onRetry(item.accession)}
          >
            <RotateCcw className="size-3.5" />
          </button>
        </>
      )}
    </div>
  );
}

type StatusIconProps = {
  status: AccessionStatus;
};

function StatusIcon({ status }: StatusIconProps) {
  if (status === "fetching") {
    return <Loader2 className="size-4 animate-spin text-muted-foreground" />;
  }

  if (status === "complete") {
    return <Check className="size-4 text-green-600" />;
  }

  return <X className="size-4 text-destructive" />;
}
